import type { EbookRecognitionMode } from '@wx-weiqi/contracts';
import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
  Cross2Icon,
} from '@radix-ui/react-icons';
import type { EbookDocument, EbookWorkspace, EbookQuestion } from '@wx-weiqi/contracts';
import {
  EbookClient,
  loadEbookOverview,
  loadPublishedEbook,
  ebookFileProblem,
} from '../services/ebooks';
import { EbookEditor, EbookSource } from './EbookEditor';
import { EbookReader, EbookThumbnail } from './EbookReader';
import { EbookPublishPanel } from './EbookPublishPanel';
import { EbookUnsavedDialog } from './EbookUnsavedDialog';
import { isEbookQuestionReady } from '../services/ebook-editing';
import { EbookQuestionLibrary } from './EbookQuestionLibrary';
import { EbookPagePicker } from './EbookPagePicker';
import { EbookArrangePanel } from './EbookArrangePanel';
import { StudentBookshelf, type BookshelfViewState } from './StudentBookshelf';
import { EbookDeleteDialog } from './EbookDeleteDialog';
import { EbookWorkflowNav } from './EbookWorkflowNav';
import { secureRandomUuid } from '../services/random-id';
import { latestEbookQuestions } from '../services/ebook-pages';
import { ebookChapterNames } from '../services/ebook-chapters';
import {
  saveEbookChapterWorkspace,
  workspaceWithRenamedEbookChapter,
  workspaceWithNewEbookChapter,
} from '../services/ebook-chapter-editing';
import './ebook.css';
import './ebook-teacher.css';
import { EbookStudyRecords } from './EbookStudyRecords';
import { EbookRankings } from './EbookRankings';
import './ebook-workspace.css';
import './ebook-study.css';
import './ebook-workspace-shell.css';
import { TeacherTextbookShelf } from './TeacherTextbookShelf';
import { EbookPagedAuthoring } from './EbookPagedAuthoring';
import { EbookQuestionWriter } from '../services/ebook-question-writer';
import {
  TeacherTextbookNavigation,
  type TeacherTextbookNavigationProps,
} from './TeacherTextbookNavigation';
type Shelf = Awaited<ReturnType<EbookClient['list']>>;
export function EbooksPage({
  client,
  role,
  onBack,
  teacherNavigation,
  rankingsOpen,
  onRankingsOpen,
  onRankingsClose,
}: {
  client: EbookClient;
  role: 'TEACHER' | 'STUDENT';
  onBack: () => void;
  teacherNavigation?: TeacherTextbookNavigationProps | undefined;
  rankingsOpen?: boolean;
  onRankingsOpen?: () => void;
  onRankingsClose?: () => void;
}) {
  const [localRankingsOpen, setLocalRankingsOpen] = useState(false);
  const openRankings = onRankingsOpen ?? (() => setLocalRankingsOpen(true));
  const closeRankings = () => {
    setLocalRankingsOpen(false);
    onRankingsClose?.();
  };
  const [shelf, setShelf] = useState<Shelf>([]);
  const [deleteItem, setDeleteItem] = useState<Shelf[number] | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [limits, setLimits] = useState<Awaited<ReturnType<EbookClient['limits']>> | null>(null);
  const [doc, setDoc] = useState<EbookDocument | null>(null);
  const [pagedDocumentId, setPagedDocumentId] = useState<string | null>(null);
  const questionWriter = useRef(new EbookQuestionWriter());
  const [draft, setDraft] = useState<EbookWorkspace | null>(null);
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState('pages');
  const [editorHeaderHidden, setEditorHeaderHidden] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const uploadDialog = useRef<HTMLDialogElement>(null);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [working, setBusy] = useState(false);
  const [chapterRefreshPending, setChapterRefreshPending] = useState(false);
  const creatingChapter = useRef(false);
  const draftGeneration = useRef(0);
  const chapterRefreshButton = useRef<HTMLButtonElement>(null);
  const [editorBusy, setEditorBusy] = useState(false);
  const busy = working || chapterRefreshPending || editorBusy;
  const [error, setError] = useState('');
  const [shelfError, setShelfError] = useState('');
  const [limitsError, setLimitsError] = useState('');
  const [notice, setNotice] = useState('');
  const [password, setPassword] = useState('');
  const [range, setRange] = useState('');
  const [uploadPageScope, setUploadPageScope] = useState<'all' | 'range'>('all');
  const [file, setFile] = useState<File | null>(null);
  const [classes, setClasses] = useState<Awaited<ReturnType<EbookClient['classes']>>>([]);
  const [classIds, setClassIds] = useState<string[]>([]);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [rights, setRights] = useState(false);
  const [releases, setReleases] = useState<Awaited<ReturnType<EbookClient['releases']>>>([]);
  const [publicationRefreshPending, setPublicationRefreshPending] = useState(false);
  const [reader, setReader] = useState<string | null>(null);
  const lastShelfBook = useRef<string | null>(null);
  const shelfView = useRef<BookshelfViewState | null>(null);
  const [preview, setPreview] = useState(false);
  const [previewQuestionId, setPreviewQuestionId] = useState<string>();
  const [revisionList, setRevisionList] = useState<Awaited<ReturnType<EbookClient['revisions']>>>(
    [],
  );
  const fileProblem = file && limits ? ebookFileProblem(file, limits.maxBytes) : '';

  useEffect(() => {
    if (!notice || chapterRefreshPending || publicationRefreshPending) return;
    const timer = window.setTimeout(() => setNotice(''), 5000);
    return () => window.clearTimeout(timer);
  }, [notice, chapterRefreshPending, publicationRefreshPending]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => {
    if (!uploadOpen) return;
    const dialog = uploadDialog.current;
    const previous = document.activeElement;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [uploadOpen]);

  useEffect(() => {
    if (chapterRefreshPending) chapterRefreshButton.current?.focus();
  }, [chapterRefreshPending]);

  const run = async (fn: () => Promise<void>) => {
    if (creatingChapter.current) return;
    if (chapterRefreshPending) {
      setError('章节与草稿已保存，请先点击“刷新已保存草稿”取得最新版本，再继续操作。');
      return;
    }
    draftGeneration.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (e) {
      setError(userErrorMessage(e, '操作失败'));
    } finally {
      setBusy(false);
    }
  };
  const refresh = async () => {
    const result = await loadEbookOverview(client);
    if (result.shelf) setShelf(result.shelf);
    setLimits(result.limits);
    setShelfError(result.shelfError);
    setLimitsError(result.limitsError);
  };
  useEffect(() => {
    void run(refresh);
    if (role === 'TEACHER')
      void client
        .classes()
        .then(setClasses)
        .catch(() => {});
    const linked = new URLSearchParams(window.location.search).get('ebook');
    if (linked) setReader(linked);
  }, [client, role]);
  useEffect(() => {
    if (
      !doc ||
      dirty ||
      busy ||
      !doc.pages.some((p) => p.status === 'QUEUED' || p.status === 'RUNNING') ||
      doc.cancelled
    )
      return;
    let alive = true;
    const timer = setInterval(() => {
      const generation = draftGeneration.current;
      void client
        .document(doc.id)
        .then((d) => {
          if (!alive || generation !== draftGeneration.current) return;
          setDoc(d);
          setDraft(d.data);
        })
        .catch((e) => {
          if (alive && generation === draftGeneration.current)
            setError(userErrorMessage(e, '刷新失败'));
        });
    }, 2500);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [client, doc, dirty, busy]);
  const openDocument = async (
    id: string,
    legacy = false,
    initialStep = 'pages',
    initialQuestion?: string,
  ) => {
    if (!legacy && shelf.find((item) => item.id === id)?.draftCount !== undefined) {
      setPagedDocumentId(id);
      return;
    }
    const [d, publications] = await Promise.all([client.document(id), client.releases(id)]);
    setDoc(d);
    setDraft(d.data);
    setRevisionList([]);
    setDirty(false);
    setQuestionId(initialQuestion ?? null);
    setPage(d.pages.find((p) => p.status !== 'SKIPPED')?.index ?? 0);
    setStep(initialStep);
    setPagedDocumentId(null);
    setEditorHeaderHidden(false);
    setPublicationRefreshPending(false);
    setChapterRefreshPending(false);
    setReleases(publications);
    setClassIds(publications[0]?.classIds ?? []);
    setStudentIds(publications[0]?.studentIds ?? []);
    setRights(false);
  };
  const change = (data: EbookWorkspace) => {
    if (chapterRefreshPending || creatingChapter.current) return;
    draftGeneration.current++;
    setDraft(data);
    setDirty(true);
  };
  const refreshPublication = async (id: string, preserveDraft: boolean) => {
    const result = await loadPublishedEbook(client, id);
    // Keep the draft's version too: adopting a newer version while preserving
    // local edits would bypass the server's concurrent-edit conflict check.
    if (result.document && !preserveDraft) {
      setDoc(result.document);
      setDraft(result.document.data);
    }
    if (result.releases) setReleases(result.releases);
    setPublicationRefreshPending(result.incomplete);
    setNotice(
      result.incomplete
        ? '电子书已发布成功，页面状态暂未刷新。请点击“刷新发布状态”，无需再次发布。'
        : '电子书已更新。学生从原入口重新打开即可看到最新内容，已有作答记录保留。',
    );
  };
  const q = draft?.questions.find((q) => q.id === questionId);
  // Teacher choices include draft questions not yet selected for the published book.
  const chapters = draft
    ? ebookChapterNames(
        { ...draft.book, questionIds: draft.questions.map((question) => question.id) },
        draft.questions,
      )
    : [];
  const latestQuestions = doc && draft ? latestEbookQuestions(doc.pages, draft.questions) : [];
  const isLatestQuestion = latestQuestions.some((item) => item.id === questionId);
  // A successful write followed by a failed refresh must never be resubmitted
  // against the stale version. Keep the accepted draft and require a read first.
  const persistDraft = async (data: EbookWorkspace) => {
    if (!doc) throw new Error('当前教材未加载。');
    const changed = data.questions.filter((item, i) => item !== doc.data.questions[i]);
    if (
      data.book === doc.data.book &&
      data.relations === doc.data.relations &&
      data.questions.length === doc.data.questions.length &&
      changed.length === 1 &&
      changed[0]!.id === doc.data.questions[data.questions.indexOf(changed[0]!)]?.id &&
      shelf.find((item) => item.id === doc.id)?.draftCount !== undefined
    ) {
      const result = await questionWriter.current.save(
        client,
        doc.id,
        doc.stateVersion,
        changed[0]!,
      );
      const next = {
        ...doc,
        stateVersion: result.stateVersion,
        data: {
          ...data,
          questions: data.questions.map((item) =>
            item.id === result.question.id ? result.question : item,
          ),
        },
      };
      setDoc(next);
      setDraft(next.data);
      setDirty(false);
      setChapterRefreshPending(false);
      return next;
    }
    const saved = await saveEbookChapterWorkspace(client, doc, data);
    setDraft(saved.document?.data ?? saved.workspace);
    setDirty(false);
    setChapterRefreshPending(!saved.document);
    if (!saved.document) {
      setPendingNavigation(null);
      setNotice('草稿已保存成功，暂未取得最新版本。请刷新已保存草稿后继续，无需重复保存。');
      throw new Error('草稿已保存成功，请先刷新已保存草稿，再继续操作。');
    }
    setDoc(saved.document);
    return saved.document;
  };
  const save = async () => {
    if (!doc || !draft) return;
    await persistDraft(draft);
    setNotice('草稿已保存。已发布教材需更新电子书后学生才可见。');
  };
  const saveQuestion = async (question: EbookQuestion, complete = false) => {
    if (!doc || !draft) return;
    if (chapterRefreshPending) throw new Error('草稿已保存，请先刷新已保存草稿，再继续编辑。');
    draftGeneration.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const next = await persistDraft({
        ...draft,
        questions: draft.questions.map((item) => (item.id === question.id ? question : item)),
      });
      if (complete) {
        const saved = next.data.questions.find((item) => item.id === question.id);
        if (!saved || !isEbookQuestionReady(saved))
          throw new Error('草稿已保存；请重新确认棋形和答案后完成入库。');
        setNotice('分支已保存。');
      } else setNotice('题目信息已保存。');
    } finally {
      setBusy(false);
    }
  };
  const saveFromPanel = async () => {
    if (busy) throw new Error('正在处理，请稍候。');
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await save();
    } catch (e) {
      setError(userErrorMessage(e, '保存失败，修改仍保留，请重试。'));
    } finally {
      setBusy(false);
    }
  };
  const requestNavigation = (action: () => void) => {
    if (busy) return;
    if (dirty) setPendingNavigation(() => action);
    else action();
  };
  const saveAndNavigate = () =>
    void run(async () => {
      await save();
      const navigate = pendingNavigation;
      setPendingNavigation(null);
      navigate?.();
    });
  const selectQuestion = (id: string) => requestNavigation(() => setQuestionId(id));
  const returnToShelf = () =>
    requestNavigation(() => {
      setDoc(null);
      setDraft(null);
      setNotice('');
      setError('');
      void run(refresh);
    });
  const createChapter = async (name: string, question: EbookQuestion) => {
    if (!doc || !draft) throw new Error('当前教材未加载，请重新打开后再新增章节。');
    if (busy || creatingChapter.current) throw new Error('正在处理草稿，请稍候再试。');
    const data = workspaceWithNewEbookChapter(draft, question, name, chapters);
    creatingChapter.current = true;
    draftGeneration.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await saveEbookChapterWorkspace(client, doc, data);
      if (saved.document) setDoc(saved.document);
      setDraft(saved.document?.data ?? saved.workspace);
      setDirty(false);
      setChapterRefreshPending(!saved.document);
      setNotice(
        saved.document
          ? '章节和当前全部草稿已保存，本题已归入新章节。已发布教材需“更新电子书”后学生才可见。'
          : '章节和当前全部草稿已保存成功，暂未取得最新版本。无需再次创建；请刷新已保存草稿后继续编辑。',
      );
    } finally {
      creatingChapter.current = false;
      setBusy(false);
    }
  };
  const renameChapter = async (currentName: string, nextName: string) => {
    if (!doc || !draft) throw new Error('当前教材未加载，请重新打开后再修改章节。');
    if (busy || creatingChapter.current) throw new Error('正在处理草稿，请稍候再试。');
    const data = workspaceWithRenamedEbookChapter(draft, currentName, nextName, chapters);
    creatingChapter.current = true;
    draftGeneration.current++;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await saveEbookChapterWorkspace(client, doc, data);
      if (saved.document) setDoc(saved.document);
      setDraft(saved.document?.data ?? saved.workspace);
      setDirty(false);
      setChapterRefreshPending(!saved.document);
      setNotice(
        saved.document
          ? `章节已修改为“${nextName}”，对应题目已全部同步。已发布教材需“更新电子书”后学生才可见。`
          : '章节和题目已保存成功，暂未取得最新版本。请刷新已保存草稿后继续编辑。',
      );
    } finally {
      creatingChapter.current = false;
      setBusy(false);
    }
  };
  const refreshChapterDraft = async () => {
    if (!doc || working) return;
    draftGeneration.current++;
    setBusy(true);
    setError('');
    try {
      const next = await client.document(doc.id);
      setDoc(next);
      setDraft(next.data);
      setDirty(false);
      setChapterRefreshPending(false);
      setNotice('已刷新保存后的最新草稿，可以继续编辑。已发布教材需“更新电子书”后学生才可见。');
    } catch {
      setError(
        '章节和草稿已保存，但最新版本仍未读取成功。请检查网络后再次刷新，无需重新创建章节。',
      );
    } finally {
      setBusy(false);
    }
  };
  const command = async (action: string, extra: Record<string, unknown> = {}) => {
    if (!doc) return;
    await client.mutate(doc.id, { action, expectedVersion: doc.stateVersion, ...extra });
    const next = await client.document(doc.id);
    setDoc(next);
    setDraft(next.data);
    setDirty(false);
  };
  const retryFailed = async () => {
    if (!doc || !draft) return;
    let current = doc;
    if (dirty) {
      await client.save(doc.id, doc.stateVersion, draft);
      current = await client.document(doc.id);
      setDoc(current);
      setDraft(current.data);
      setDirty(false);
    }
    await client.mutate(current.id, {
      action: 'RETRY_FAILED',
      expectedVersion: current.stateVersion,
    });
    const next = await client.document(current.id);
    setDoc(next);
    setDraft(next.data);
    setDirty(false);
    setNotice('失败页已重新排队；原件及教师修改均已保留。');
  };
  const processPages = async (pageIndexes: number[]) => {
    if (!doc || !draft) return;
    let current = doc;
    if (dirty) {
      await client.save(doc.id, doc.stateVersion, draft);
      current = await client.document(doc.id);
      setDoc(current);
      setDraft(current.data);
      setDirty(false);
    }
    await client.mutate(current.id, {
      action: 'PROCESS_PAGES',
      expectedVersion: current.stateVersion,
      pageIndexes,
    });
    const next = await client.document(current.id);
    setDoc(next);
    setDraft(next.data);
    setDirty(false);
    setNotice('已按勾选页安排处理；未选页的排队任务已停止，已完成结果和教师修改保留。');
  };
  const optimizePage = async (pageIndex: number, mode: EbookRecognitionMode) => {
    if (!doc || !draft) return;
    let current = doc;
    if (dirty) {
      await client.save(doc.id, doc.stateVersion, draft);
      current = await client.document(doc.id);
      setDoc(current);
      setDraft(current.data);
      setDirty(false);
    }
    await client.mutate(current.id, {
      action: 'OPTIMIZE_PAGE',
      mode,
      expectedVersion: current.stateVersion,
      pageIndex,
    });
    const next = await client.document(current.id);
    setDoc(next);
    setDraft(next.data);
    setDirty(false);
    setNotice('已安排本页优化识别；原 PDF 和教师修改均已保留。');
  };
  const merge = async () => {
    if (!doc || !draft) return;
    const next = await client.document(doc.id);
    for (const old of doc.data.questions) {
      const local = draft.questions.find((q) => q.id === old.id),
        remote = next.data.questions.find((q) => q.id === old.id);
      if (
        local &&
        JSON.stringify(local) !== JSON.stringify(old) &&
        remote &&
        remote.revision !== old.revision
      )
        throw new Error('同一道题已被另一端修改，请先下载草稿备份并逐题比较');
    }
    const ids = new Set(draft.questions.map((q) => q.id));
    setDoc(next);
    setDraft({
      ...draft,
      questions: [...draft.questions, ...next.data.questions.filter((q) => !ids.has(q.id))],
    });
    setNotice('已合并新识别候选，请检查后保存。');
  };
  const region = (
    r: { x: number; y: number; width: number; height: number },
    sourcePage = q?.source?.pageIndex ?? page,
  ) => {
    void run(async () => {
      if (dirty) throw new Error('请先保存草稿再重识别');
      await command('RECOGNIZE', { pageIndex: sourcePage, region: r });
      setNotice('已追加区域识别任务；教师修订保持独立。');
    });
  };
  const uploadCover = (file: File) =>
    void run(async () => {
      if (!doc || !draft) return;
      let current = doc;
      if (dirty) {
        await client.save(doc.id, doc.stateVersion, draft);
        current = await client.document(doc.id);
        setDoc(current);
        setDraft(current.data);
        setDirty(false);
      }
      await client.uploadCover(doc.id, current.stateVersion, file);
      const next = await client.document(doc.id);
      setDoc(next);
      setDraft(next.data);
      setDirty(false);
      setNotice('封面已保存。发布或更新电子书后，学生书架会展示新封面。');
    });
  const goStep = (next: string) =>
    requestNavigation(() => {
      setNotice('');
      if (next === 'edit' && !q && latestQuestions.length)
        setQuestionId(
          (latestQuestions.find((item) => !item.faceConfirmed) ?? latestQuestions[0])!.id,
        );
      setStep(next);
      window.scrollTo({ top: 0 });
    });
  if (role === 'STUDENT' && (rankingsOpen ?? localRankingsOpen))
    return (
      <EbookRankings
        client={client}
        onBack={closeRankings}
        onOpenBook={(id) => {
          setReader(id);
          closeRankings();
        }}
      />
    );
  if (reader)
    return (
      <EbookReader
        client={client}
        releaseId={reader}
        onBack={() => {
          lastShelfBook.current = reader;
          setReader(null);
          void run(refresh);
        }}
      />
    );
  if (preview && doc && draft)
    return (
      <EbookReader
        client={client}
        preview={{ documentId: doc.id, data: draft }}
        {...(previewQuestionId ? { initialQuestionId: previewQuestionId } : {})}
        onBack={() => setPreview(false)}
      />
    );
  const teacherShelf = role === 'TEACHER' && !doc;
  if (pagedDocumentId)
    return (
      <EbookPagedAuthoring
        key={pagedDocumentId}
        client={client}
        documentId={pagedDocumentId}
        onBack={() => {
          setPagedDocumentId(null);
          setDoc(null);
          setDraft(null);
          void run(refresh);
        }}
        onLegacy={(nextStep, id) => openDocument(pagedDocumentId, true, nextStep, id)}
      />
    );
  const content = (
    <>
      {!teacherShelf && (
        <header className="ebook-header">
          {role === 'TEACHER' ? (
            <>
              <button
                className="ebook-back"
                disabled={busy}
                onClick={() => (doc ? returnToShelf() : onBack())}
              >
                <ChevronLeftIcon aria-hidden="true" />
                {doc ? '返回教材' : '返回课堂'}
              </button>
              <h1 title={doc ? draft?.book.title : '我的教材'}>
                {doc ? draft?.book.title : '我的教材'}
              </h1>
              <div className="ebook-header-actions">
                {doc && (
                  <span className={`ebook-save-state${dirty ? ' is-dirty' : ''}`} role="status">
                    {working
                      ? '正在处理…'
                      : chapterRefreshPending
                        ? '已保存 · 待刷新'
                        : dirty
                          ? '有未保存修改'
                          : '草稿已保存'}
                  </span>
                )}
                <details className="ebook-workspace-more">
                  <summary>
                    更多 <DotsHorizontalIcon aria-hidden="true" />
                  </summary>
                  <div className="ebook-workspace-menu">
                    {doc ? (
                      <>
                        <button disabled={busy || !dirty} onClick={() => void run(save)}>
                          保存全部草稿
                        </button>
                        <button
                          disabled={busy || step === 'records'}
                          onClick={() =>
                            void run(async () => setRevisionList(await client.revisions(doc.id)))
                          }
                        >
                          修订历史
                        </button>
                        <button disabled={busy || !dirty} onClick={() => void run(merge)}>
                          合并新识别候选
                        </button>
                        <button
                          disabled={busy}
                          onClick={() =>
                            void run(() =>
                              client.download(`/documents/${doc.id}/original`, doc.name),
                            )
                          }
                        >
                          下载原 PDF
                        </button>
                        <small>
                          {doc.name} · {doc.pageCount} 页
                        </small>
                      </>
                    ) : (
                      <button disabled={busy} onClick={() => void run(refresh)}>
                        刷新书架
                      </button>
                    )}
                  </div>
                </details>
              </div>
            </>
          ) : (
            <>
              <button disabled={busy} onClick={onBack}>
                <ArrowLeftIcon aria-hidden="true" />
                返回
              </button>
              <button className="student-bookshelf__ranking" onClick={openRankings}>
                排名
              </button>
            </>
          )}
        </header>
      )}
      {pendingNavigation && (
        <EbookUnsavedDialog
          busy={busy}
          error={error}
          onStay={() => setPendingNavigation(null)}
          onSave={saveAndNavigate}
          onDiscard={() => {
            setDraft(doc?.data ?? null);
            setDirty(false);
            setPendingNavigation(null);
            pendingNavigation();
          }}
        />
      )}
      {error && (
        <p role="alert" className="ebook-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="ebook-notice">
          {notice}
        </p>
      )}
      {chapterRefreshPending && (
        <button
          ref={chapterRefreshButton}
          disabled={working}
          onClick={() => void refreshChapterDraft()}
        >
          {working ? '正在刷新…' : '刷新已保存草稿'}
        </button>
      )}
      {!doc ? (
        <>
          {role === 'TEACHER' && deleteItem && (
            <EbookDeleteDialog
              client={client}
              item={deleteItem}
              onClose={() => setDeleteItem(null)}
              onDeleted={() => {
                setShelf((items) => items.filter((item) => item.id !== deleteItem.id));
                setNotice(`已删除教材“${deleteItem.name}”及其全部数据。`);
                setError('');
                setDeleteItem(null);
              }}
            />
          )}
          {role === 'TEACHER' && (
            <dialog
              ref={uploadDialog}
              id="ebook-upload-panel"
              className="ebook-teacher ebook-import-panel"
              aria-labelledby="ebook-import-title"
              onCancel={(event) => {
                event.preventDefault();
                if (!busy) setUploadOpen(false);
              }}
            >
              <header className="ebook-import-heading">
                <h2 id="ebook-import-title">导入教材</h2>
                <button
                  aria-label="关闭导入教材"
                  disabled={busy}
                  onClick={() => setUploadOpen(false)}
                >
                  <Cross2Icon />
                </button>
              </header>
              <div className="ebook-drop">
                <p className="ebook-muted">
                  选择 PDF 和处理页，识别后再校对。原文件仅授权教师可见。
                </p>
                <label>
                  选择 PDF
                  <input
                    aria-label="选择 PDF"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                {file && (
                  <p
                    className={fileProblem ? 'ebook-error' : 'ebook-muted'}
                    role={fileProblem ? 'alert' : undefined}
                  >
                    {fileProblem || `${file.name} · ${(file.size / 1048576).toFixed(2)} MiB`}
                  </p>
                )}
                <div className="ebook-compact">
                  <fieldset className="ebook-import-scope">
                    <legend>先处理哪些页面</legend>
                    <label>
                      <input
                        type="radio"
                        name="upload-page-scope"
                        checked={uploadPageScope === 'all'}
                        onChange={() => setUploadPageScope('all')}
                      />
                      整本教材
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="upload-page-scope"
                        checked={uploadPageScope === 'range'}
                        onChange={() => setUploadPageScope('range')}
                      />
                      指定页码
                    </label>
                    {uploadPageScope === 'range' && (
                      <label className="ebook-import-page-range">
                        <span>PDF 页码</span>
                        <input
                          placeholder="例如 1-5,8"
                          value={range}
                          onChange={(e) => setRange(e.target.value)}
                        />
                      </label>
                    )}
                  </fieldset>
                  <details>
                    <summary>文件有密码？</summary>
                    <label>
                      PDF 密码
                      <input
                        type="password"
                        autoComplete="off"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </label>
                  </details>
                </div>
                {error && (
                  <p role="alert" className="ebook-error">
                    {error}
                  </p>
                )}
                <div className="ebook-import-footer">
                  <button disabled={busy} onClick={() => setUploadOpen(false)}>
                    取消
                  </button>
                  <button
                    className="primary"
                    disabled={
                      busy ||
                      !file ||
                      (uploadPageScope === 'range' && !range.trim()) ||
                      !!fileProblem ||
                      !limits?.recognitionAvailable ||
                      !limits.storageAvailable
                    }
                    onClick={() => {
                      void run(async () => {
                        if (!file) return;
                        const result = await client.upload(
                          file,
                          password,
                          uploadPageScope === 'range' ? range : '',
                        );
                        setPassword('');
                        await openDocument(result.id);
                        setFile(null);
                        setRange('');
                        setUploadPageScope('all');
                        setUploadOpen(false);
                      });
                    }}
                  >
                    {busy ? '正在上传…' : '上传并识别'}
                  </button>
                </div>
              </div>
              <aside className="ebook-import-info">
                <h3>上传须知</h3>
                <p>页码从 PDF 第 1 页起，可填写不连续的页码，例如 4–8,12。</p>
                {limits && (
                  <p>
                    每份 ≤ {Math.round(limits.maxBytes / 1048576)} MiB · ≤ {limits.maxPages} 页
                    <br />
                    每次最多处理 {limits.maxPagesPerRun} 页
                  </p>
                )}
                <details>
                  <summary>识别能力与处理限制</summary>
                  <p>{limits?.message}</p>
                  {limits && (
                    <p>
                      单页 ≤ {limits.maxPixels.toLocaleString()} 像素 ·{' '}
                      {limits.pageTimeoutMs / 1000} 秒
                    </p>
                  )}
                </details>
              </aside>
            </dialog>
          )}
          {role === 'TEACHER' &&
            (limitsError ||
              (limits && (!limits.recognitionAvailable || !limits.storageAvailable))) && (
              <div className="ebook-error" role="alert">
                {limitsError && <p>识别服务状态加载失败：{limitsError}</p>}
                {limits && !limits.storageAvailable && <p>{limits.storageMessage}</p>}
                {limits && !limits.recognitionAvailable && <p>{limits.message}</p>}
                <p>暂不能上传新教材；已有教材仍可打开。</p>
                <button disabled={busy} onClick={() => void run(refresh)}>
                  重新检查服务
                </button>
              </div>
            )}
          {role === 'TEACHER' ? (
            <TeacherTextbookShelf
              client={client}
              onTotalSaved={(result) =>
                setShelf((items) =>
                  items.map((item) =>
                    item.id === result.id && item.stateVersion <= result.stateVersion
                      ? { ...item, ...result }
                      : item,
                  ),
                )
              }
              items={shelf}
              busy={busy}
              error={shelfError}
              onOpen={(id) => void run(() => openDocument(id))}
              onDelete={setDeleteItem}
              onImport={() => {
                setError('');
                setUploadOpen(true);
              }}
              onRefresh={() => void run(refresh)}
            />
          ) : (
            <StudentBookshelf
              initialBookId={lastShelfBook.current}
              initialView={shelfView.current}
              onViewChange={(view) => {
                shelfView.current = view;
              }}
              items={shelf}
              client={client}
              busy={busy}
              error={shelfError}
              onOpen={setReader}
              onRefresh={() => void run(refresh)}
            />
          )}
        </>
      ) : (
        draft && (
          <div className={`ebook-workspace-shell is-${step}`}>
            <EbookWorkflowNav
              step={step}
              onStep={goStep}
              busy={busy}
              document={doc}
              data={draft}
              published={releases.length > 0}
            />
            <div className="ebook-workspace-body">
              {step !== 'records' && revisionList.length > 0 && (
                <details open>
                  <summary>历史快照（恢复后需再次保存）</summary>
                  <div className="ebook-actions">
                    {revisionList.map((r) => (
                      <button
                        key={r.version}
                        onClick={() => {
                          void run(async () => {
                            const restored = await client.revision(doc.id, r.version);
                            restored.book.coverImage ??= null;
                            restored.book.chapters ??= [];
                            change(restored);
                            setRevisionList([]);
                          });
                        }}
                      >
                        修订 {r.version}
                      </button>
                    ))}
                  </div>
                </details>
              )}
              {step === 'records' && (
                <EbookStudyRecords
                  key={doc.id}
                  client={client}
                  documentId={doc.id}
                  published={releases.length > 0}
                  onPublish={() => goStep('publish')}
                />
              )}
              {step === 'pages' && (
                <section className="ebook-recognition-workspace" aria-label="页面识别">
                  <div className="ebook-task-heading">
                    <div>
                      <h2>选好页面，开始校对</h2>
                      <p>
                        共 {doc.pageCount} 页 · 已识别{' '}
                        {doc.pages.filter((p) => p.status === 'DONE').length} 页 · 待处理{' '}
                        {
                          doc.pages.filter((p) =>
                            ['SKIPPED', 'QUEUED', 'RUNNING'].includes(p.status),
                          ).length
                        }{' '}
                        页 · 失败 {doc.pages.filter((p) => p.status === 'FAILED').length} 页
                        {doc.cancelled ? ' · 识别已暂停' : ''}
                      </p>
                    </div>
                    <details className="ebook-workspace-more">
                      <summary>
                        识别任务 <DotsHorizontalIcon />
                      </summary>
                      <div className="ebook-workspace-menu">
                        <button
                          disabled={
                            busy ||
                            dirty ||
                            doc.cancelled ||
                            !doc.pages.some((p) => ['QUEUED', 'RUNNING'].includes(p.status))
                          }
                          onClick={() => void run(() => command('CANCEL'))}
                        >
                          暂停识别
                        </button>
                        <button
                          disabled={busy || !doc.pages.some((p) => p.status === 'FAILED')}
                          onClick={() => void run(retryFailed)}
                        >
                          重试失败页
                        </button>
                      </div>
                    </details>
                  </div>
                  <div className="ebook-recognition-columns">
                    <aside className="ebook-recognition-pages">
                      <EbookPagePicker
                        key={doc.id}
                        pages={doc.pages}
                        page={page}
                        onPage={setPage}
                        busy={busy}
                        dirty={dirty}
                        budget={limits?.maxPagesPerRun ?? 40}
                        onProcess={(indexes) => void run(() => processPages(indexes))}
                      />
                      {doc.pages[page]?.error && (
                        <p className="ebook-error">
                          {userErrorMessage(doc.pages[page]?.error, '页面识别失败，请重试。')}
                        </p>
                      )}
                    </aside>
                    <section className="ebook-recognition-preview">
                      <EbookSource
                        client={client}
                        document={doc}
                        pageIndex={page}
                        question={null}
                        busy={busy}
                        recognitionAvailable={limits?.recognitionAvailable ?? false}
                        onOptimize={(mode) => run(() => optimizePage(page, mode))}
                        onRegion={(r) => {
                          setQuestionId(null);
                          region(r, page);
                        }}
                      />
                    </section>
                    <aside className="ebook-recognition-questions">
                      <h3>
                        本页识别出{' '}
                        {latestQuestions.filter((item) => item.source?.pageIndex === page).length}{' '}
                        道题
                      </h3>
                      <div className="ebook-recognition-question-list">
                        {latestQuestions
                          .filter((item) => item.source?.pageIndex === page)
                          .map((item) => (
                            <button
                              key={item.id}
                              disabled={busy}
                              onClick={() =>
                                requestNavigation(() => {
                                  setQuestionId(item.id);
                                  setStep('edit');
                                })
                              }
                            >
                              <EbookThumbnail question={item} />
                              <span>
                                <strong>第 {item.number || '—'} 题</strong>
                                <small>{item.faceConfirmed ? '棋形已确认' : '待确认棋形'}</small>
                              </span>
                              <ChevronRightIcon />
                            </button>
                          ))}
                        {!latestQuestions.some((item) => item.source?.pageIndex === page) && (
                          <p className="ebook-muted">
                            {doc.pages[page]?.status === 'DONE'
                              ? '未发现题图。可在原页中补框重识别。'
                              : '识别完成后，题目会显示在这里。'}
                          </p>
                        )}
                      </div>
                      <p className="ebook-recognition-hint">先检查棋形中的红色疑点，再录入答案。</p>
                    </aside>
                  </div>
                  <footer className="ebook-workspace-footer">
                    <span>原 PDF 仅授权教师可见 · 漏题可在原页补框</span>
                    <button
                      className="primary"
                      disabled={busy || !latestQuestions.length}
                      onClick={() => {
                        const first =
                          latestQuestions.find((item) => item.source?.pageIndex === page) ??
                          latestQuestions[0];
                        if (first)
                          requestNavigation(() => {
                            setQuestionId(first.id);
                            setStep('edit');
                          });
                      }}
                    >
                      开始校对 <ChevronRightIcon />
                    </button>
                  </footer>
                </section>
              )}
              {step === 'edit' && (
                <>
                  <div className="ebook-row ebook-question-picker">
                    <label>
                      选择题目
                      <select
                        aria-label="选择题目"
                        disabled={busy}
                        value={isLatestQuestion ? (questionId ?? '') : ''}
                        onChange={(e) => selectQuestion(e.target.value)}
                      >
                        <option value="">请选择各页最新识别的题目草稿</option>
                        {latestQuestions.map((q, i) => (
                          <option key={q.id} value={q.id}>
                            {i + 1}. {q.title} · PDF {(q.source?.pageIndex ?? 0) + 1} 页 ·{' '}
                            {q.faceConfirmed ? '已确认' : '待校对'}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="ebook-question-switch">
                      <button
                        aria-label="上一题"
                        disabled={
                          busy || latestQuestions.findIndex((item) => item.id === questionId) <= 0
                        }
                        onClick={() =>
                          selectQuestion(
                            latestQuestions[
                              latestQuestions.findIndex((item) => item.id === questionId) - 1
                            ]!.id,
                          )
                        }
                      >
                        <ChevronLeftIcon />
                      </button>
                      <button
                        aria-label="下一题"
                        disabled={
                          busy ||
                          latestQuestions.findIndex((item) => item.id === questionId) < 0 ||
                          latestQuestions.findIndex((item) => item.id === questionId) >=
                            latestQuestions.length - 1
                        }
                        onClick={() =>
                          selectQuestion(
                            latestQuestions[
                              latestQuestions.findIndex((item) => item.id === questionId) + 1
                            ]!.id,
                          )
                        }
                      >
                        <ChevronRightIcon />
                      </button>
                      <span>共 {latestQuestions.length} 题</span>
                    </div>
                    <button
                      className="ebook-editor-header-toggle"
                      aria-expanded={!editorHeaderHidden}
                      title={
                        editorHeaderHidden
                          ? '恢复教材标题与流程导航'
                          : '隐藏教材标题与流程导航，为棋盘腾出空间'
                      }
                      onClick={() => setEditorHeaderHidden((hidden) => !hidden)}
                    >
                      {editorHeaderHidden ? (
                        <ChevronDownIcon aria-hidden="true" />
                      ) : (
                        <ChevronUpIcon aria-hidden="true" />
                      )}
                      {editorHeaderHidden ? '显示上方内容' : '隐藏上方内容'}
                    </button>
                    {q && (
                      <details className="ebook-question-more">
                        <summary>题目操作与导出</summary>
                        <p className="ebook-muted">
                          选择器仅显示各页最新识别结果；保留题目请到教材题库查找。
                        </p>
                        <div className="ebook-actions">
                          <button
                            disabled={busy}
                            onClick={() => {
                              const id = secureRandomUuid();
                              change({
                                ...draft,
                                questions: [
                                  ...draft.questions,
                                  {
                                    ...q,
                                    id,
                                    candidateId: null,
                                    revision: 1,
                                    title: q.title + '（副本）',
                                    faceConfirmed: false,
                                    answerConfirmed: false,
                                  },
                                ],
                              });
                              setQuestionId(id);
                            }}
                          >
                            复制题目
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => {
                              change({
                                ...draft,
                                questions: draft.questions.filter((p) => p.id !== q.id),
                                relations: draft.relations.filter(
                                  (r) => r.sourceId !== q.id && r.targetId !== q.id,
                                ),
                                book: {
                                  ...draft.book,
                                  questionIds: draft.book.questionIds.filter((id) => id !== q.id),
                                },
                              });
                              setQuestionId(null);
                            }}
                          >
                            删除误检题
                          </button>
                          <button
                            disabled={busy || dirty}
                            onClick={() => {
                              void run(() =>
                                client.download(
                                  `/documents/${doc.id}/sgf/${q.id}?answers=false`,
                                  'question.sgf',
                                ),
                              );
                            }}
                          >
                            题面 SGF
                          </button>
                          <button
                            disabled={busy || dirty}
                            onClick={() => {
                              void run(() =>
                                client.download(
                                  `/documents/${doc.id}/sgf/${q.id}?answers=true`,
                                  'solution.sgf',
                                ),
                              );
                            }}
                          >
                            含答案 SGF
                          </button>
                        </div>
                        <details className="ebook-relations-options">
                          <summary>多图归属 / 答案关联（需要时展开）</summary>
                          <p className="ebook-muted">
                            将答案图或变化图关联到对应题目。关联后仍需核对答案着手。
                          </p>
                          <label>
                            此图关联到
                            <select
                              value={
                                draft.relations.find((r) => r.sourceId === q.id)?.targetId ?? ''
                              }
                              onChange={(e) =>
                                change({
                                  ...draft,
                                  relations: [
                                    ...draft.relations.filter((r) => r.sourceId !== q.id),
                                    {
                                      sourceId: q.id,
                                      targetId: e.target.value || null,
                                      kind:
                                        q.role === 'ANSWER'
                                          ? 'ANSWER'
                                          : q.role === 'VARIATION'
                                            ? 'VARIATION'
                                            : 'CONTINUATION',
                                      status: 'CANDIDATE',
                                      reason: '教师指定关联，待确认',
                                    },
                                  ],
                                })
                              }
                            >
                              <option value="">不确定 / 暂不关联</option>
                              {draft.questions
                                .filter((p) => p.id !== q.id)
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.number} · {p.title} · 第 {(p.source?.pageIndex ?? 0) + 1} 页
                                  </option>
                                ))}
                            </select>
                          </label>
                          {draft.relations
                            .filter((r) => r.sourceId === q.id)
                            .map((r, i) => (
                              <label className="ebook-check" key={i}>
                                <input
                                  type="checkbox"
                                  checked={r.status === 'CONFIRMED'}
                                  disabled={!r.targetId}
                                  onChange={(e) =>
                                    change({
                                      ...draft,
                                      relations: draft.relations.map((item) =>
                                        item.sourceId === q.id
                                          ? {
                                              ...item,
                                              status: e.target.checked ? 'CONFIRMED' : 'CANDIDATE',
                                            }
                                          : item,
                                      ),
                                    })
                                  }
                                />
                                {r.reason} · {r.status === 'CONFIRMED' ? '已确认' : '待确认'}
                              </label>
                            ))}
                        </details>
                      </details>
                    )}
                  </div>
                  {((q && !isLatestQuestion) || !latestQuestions.length) && (
                    <p className="ebook-muted">
                      {q && !isLatestQuestion
                        ? '正在编辑题库中保留的题目。'
                        : '暂无识别题目，请先选择页面进行处理。'}
                    </p>
                  )}
                  {q ? (
                    <>
                      <EbookEditor
                        key={q.id}
                        client={client}
                        document={doc}
                        question={q}
                        chapters={chapters}
                        onCreateChapter={createChapter}
                        onRenameChapter={renameChapter}
                        onChange={(next) =>
                          change({
                            ...draft,
                            questions: draft.questions.map((p) => (p.id === next.id ? next : p)),
                          })
                        }
                        onPersist={(next) => saveQuestion(next)}
                        onComplete={(next) => saveQuestion(next, true)}
                        dirty={dirty}
                        onBusyChange={setEditorBusy}
                        hasNextQuestion={
                          latestQuestions.findIndex((item) => item.id === q.id) >= 0 &&
                          latestQuestions.findIndex((item) => item.id === q.id) <
                            latestQuestions.length - 1
                        }
                        onCompleteAndNext={async (next) => {
                          await saveQuestion(next, true);
                          const index = latestQuestions.findIndex((item) => item.id === next.id);
                          if (index >= 0 && index < latestQuestions.length - 1)
                            setQuestionId(latestQuestions[index + 1]!.id);
                        }}
                        onRegion={(r) => region(r)}
                        busy={busy}
                      />
                    </>
                  ) : (
                    <p className="ebook-card">
                      选择一道已识别题目开始校对。若漏检，可返回原页补框并重新识别。
                    </p>
                  )}
                </>
              )}
              {step === 'library' && (
                <EbookQuestionLibrary
                  data={draft}
                  busy={busy}
                  onEdit={(id) =>
                    requestNavigation(() => {
                      setQuestionId(id);
                      setStep('edit');
                    })
                  }
                  dirty={dirty}
                  onSave={saveFromPanel}
                  onArrange={() => goStep('arrange')}
                  onAddMany={(ids) =>
                    change({
                      ...draft,
                      book: {
                        ...draft.book,
                        questionIds: [
                          ...new Set([
                            ...draft.book.questionIds,
                            ...ids.filter((id) =>
                              draft.questions.some(
                                (item) => item.id === id && isEbookQuestionReady(item),
                              ),
                            ),
                          ]),
                        ],
                      },
                    })
                  }
                  onLocate={(index) =>
                    requestNavigation(() => {
                      setPage(index);
                      setStep('pages');
                    })
                  }
                  onToggle={(id) =>
                    change({
                      ...draft,
                      book: {
                        ...draft.book,
                        questionIds: draft.book.questionIds.includes(id)
                          ? draft.book.questionIds.filter((item) => item !== id)
                          : [...draft.book.questionIds, id],
                      },
                    })
                  }
                />
              )}
              {step === 'arrange' && (
                <EbookArrangePanel
                  client={client}
                  documentId={doc.id}
                  data={draft}
                  busy={busy}
                  onChange={change}
                  onUpload={uploadCover}
                  onPreview={(id) => {
                    setPreviewQuestionId(id);
                    setPreview(true);
                  }}
                  onEdit={(id) =>
                    requestNavigation(() => {
                      setQuestionId(id);
                      setStep('edit');
                    })
                  }
                  onPublish={() => goStep('publish')}
                  onLibrary={() => goStep('library')}
                  onSave={saveFromPanel}
                  dirty={dirty}
                />
              )}
              {step === 'publish' && (
                <EbookPublishPanel
                  client={client}
                  document={doc}
                  data={draft}
                  classes={classes}
                  classIds={classIds}
                  studentIds={studentIds}
                  rights={rights}
                  releases={releases}
                  busy={busy}
                  dirty={dirty}
                  refreshPending={publicationRefreshPending}
                  onClasses={setClassIds}
                  onRights={setRights}
                  onArrange={() => goStep('arrange')}
                  onRead={(id) => requestNavigation(() => setReader(id))}
                  onRecords={() => goStep('records')}
                  onDownload={(path, name) => void run(() => client.download(path, name))}
                  onRefresh={() => void run(() => refreshPublication(doc.id, dirty))}
                  onCopy={() => {
                    const link = `${window.location.origin}/?ebook=${encodeURIComponent(releases.at(-1)!.id)}`;
                    void navigator.clipboard
                      .writeText(link)
                      .then(() => setNotice('授权阅读链接已复制'))
                      .catch(() => setNotice(link));
                  }}
                  onPublish={() =>
                    void run(async () => {
                      const current = dirty ? await persistDraft(draft) : doc;
                      await client.mutate(doc.id, {
                        action: 'PUBLISH',
                        expectedVersion: current.stateVersion,
                        classIds,
                        studentIds,
                        rightsConfirmed: true,
                      });
                      setPublicationRefreshPending(true);
                      setDirty(false);
                      await refreshPublication(doc.id, false);
                    })
                  }
                />
              )}
            </div>
          </div>
        )
      )}
    </>
  );
  return (
    <main
      className={`ebook-page${role === 'TEACHER' ? ` ebook-teacher${doc ? ' is-document' : ' ebook-teacher-shelf-layout'}${teacherShelf && teacherNavigation ? ' has-navigation' : ''}${doc && step === 'edit' && editorHeaderHidden ? ' is-editor-focused' : ''}` : ' ebook-student-shelf-layout'}`}
      aria-busy={working}
    >
      {teacherShelf ? (
        <>
          {teacherNavigation && <TeacherTextbookNavigation {...teacherNavigation} />}
          <div className="teacher-textbook-content">{content}</div>
        </>
      ) : (
        content
      )}
    </main>
  );
}
